import { useTracking } from '@vp/react-tracking';
import { Callout, FlexBox, H3, Link, Typography, responsive } from '@vp/swan';
import { CollaborationStatus } from '@99designs/graph-utils/types';
import { FormattedMessage, __ } from '@99designs/i18n';
import { CollaborationListItemFragment } from '../queries/ListingCollaborations.generated';
import { DRAFT_BRIEF_STATUS, PENDING_FULFILMENT_STATUS } from '../utils/constants';
import { getDisplayStatus } from '../utils/getDisplayStatus';

const ResponsiveFlexBox = responsive(FlexBox);
interface RequestCardSummaryProps {
    title?: string;
    productName?: string;
    createdAt?: string | undefined | null;
    linkUrl?: string;
    status?: CollaborationStatus | string;
    revisions?: CollaborationListItemFragment['revisions'];
    requestType?: string;
    analytics?: Record<string, unknown>;
}

export const RequestCardSummary = ({
    title,
    productName,
    createdAt,
    linkUrl,
    status,
    revisions,
    requestType,
    analytics,
}: RequestCardSummaryProps) => {
    const displayStatusLabel = getDisplayStatus(status);
    const revisionNumber = (revisions?.maximum ?? 0) - (revisions?.remaining ?? 0);
    const { tracking } = useTracking();

    return (
        <ResponsiveFlexBox
            xs={{ flexDirection: 'column', alignItems: 'center', gap: '2' }}
            sm={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2' }}
        >
            {linkUrl ? (
                <Link
                    href={linkUrl}
                    skin="unstyled"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                        tracking.track('Navigation Clicked', {
                            ...analytics,
                            action: 'Navigation Clicked',
                            navigationDetail: 'project-title',
                        })
                    }
                >
                    <H3 fontSkin="title-subsection" textAlign={{ xs: 'center', sm: 'left' }}>
                        {title}
                    </H3>
                </Link>
            ) : (
                <H3 fontSkin="title-subsection" textAlign={{ xs: 'center', sm: 'left' }}>
                    {title}
                </H3>
            )}

            <ResponsiveFlexBox
                xs={{ flexDirection: 'column', alignItems: 'center', gap: '2', flexWrap: 'wrap' }}
                sm={{ flexDirection: 'row', alignItems: 'center', gap: '4', flexWrap: 'wrap' }}
            >
                {/* Callouts */}
                {(requestType === 'CollaborationListItem' ||
                    requestType === 'JobTypeAuto' ||
                    status === DRAFT_BRIEF_STATUS ||
                    status === PENDING_FULFILMENT_STATUS) &&
                    status && <Callout>{displayStatusLabel}</Callout>}

                {(requestType === 'Legacy Recreation' || requestType === 'Logo') && (
                    <Callout skin="warning">{__('Retired')}</Callout>
                )}

                {/* Product name */}
                {productName && (
                    <Typography fontSkin="body-small" textColor="subtle" display="inline">
                        {productName}
                    </Typography>
                )}
                {productName && createdAt && (
                    <Typography
                        fontSkin="body-small"
                        textColor="subtle"
                        display={{ xs: 'none', md: 'inline' }}
                        component="span"
                        aria-hidden={true}
                    >
                        |
                    </Typography>
                )}

                {/* Ordered/Created date */}
                {createdAt && (
                    <Typography fontSkin="body-small" textColor="subtle" display="inline">
                        <FormattedMessage
                            message={
                                status === DRAFT_BRIEF_STATUS
                                    ? __('Created {date}')
                                    : __('Ordered {date}')
                            }
                            values={{
                                date: new Date(createdAt).toLocaleDateString(undefined, {
                                    dateStyle: 'long',
                                }),
                            }}
                        />
                    </Typography>
                )}
                {createdAt &&
                    revisionNumber > 0 &&
                    !revisions?.isUnlimited &&
                    requestType === 'CollaborationListItem' && (
                        <Typography
                            fontSkin="body-small"
                            textColor="subtle"
                            display={{ xs: 'none', md: 'inline' }}
                            component="span"
                            aria-hidden={true}
                        >
                            |
                        </Typography>
                    )}
                {/* Revision info */}
                {revisionNumber > 0 && !revisions?.isUnlimited && (
                    <Typography fontSkin="body-small" textColor="subtle" display="inline">
                        <FormattedMessage
                            message={__('Revision {revisionNumber} of {totalRevisions}')}
                            values={{ revisionNumber, totalRevisions: revisions?.maximum }}
                        />
                    </Typography>
                )}
            </ResponsiveFlexBox>
        </ResponsiveFlexBox>
    );
};
